import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import ExtraBeeswaxFilterControlName from '@/Enums/ReportingDashboard/ExtraFilterControlName.enum';
import { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IClientControlOption, IControlOption, IExtraFilterControlOption } from '@/Interfaces/ControlOption.interface';
import { activeAdvertiserFilter } from '@/helpers/advertisers/advertiserFilters.helpers';
import { parsedAdvertisersMapper } from '@/helpers/advertisers/advertiserMappers.helpers';
import { apiTabPositionToEnum } from '@/helpers/reportingDashboard';
import { getClientsList, getWhitelabelsList } from '@/helpers/reportingDashboard/slice';
import { getAdvertisersList } from '@/helpers/shared';
import { RootState } from '@/store';
import { createSelector } from '@reduxjs/toolkit';
import { selectVisibleReportingScreenTabs } from '../currentWhitelabel.selectors';
import { selectSharedApiClients, selectSharedApiWhitelabels } from '../sharedApi.selectors';

const selectReportingDashboard = (state: RootState) => state.reportingDashboard;
const reportingDashboardClientsSelector = (state: RootState) => selectSharedApiClients(state, parsedAdvertisersMapper);
const activeClientsSelector = (state: RootState) =>
  selectSharedApiClients(state, parsedAdvertisersMapper, activeAdvertiserFilter);
const reportingDashboardWhitelabelsSelector = (state: RootState) =>
  selectSharedApiWhitelabels(state, parsedAdvertisersMapper);

export const selectReportingDashboardFilters = createSelector([selectReportingDashboard], ({ filters }) => filters);

export const selectReportingDashboardCurrentApiTab = createSelector([selectReportingDashboard], ({ apiTab }) => apiTab);

// `apiTab` stores the position among VISIBLE tabs, not the ApiTabIndex enum.
// On whitelabels that hide tabs (e.g. no Search/Social) Simplifi's position
// (4) resolves to ApiTabIndex.Search, so the per-tab advertiser filter ran
// with the wrong tab: the Simplifi tab derived `hasEsmSemAccount` advertisers
// instead of `hasSimplifiAccount` ones and the admin default view scoped to a
// list containing zero Simplifi advertisers (flank #3890). Translate
// position -> enum before any per-tab filtering, mirroring getEffectiveFilters.
export const selectReportingDashboardCurrentApiTabEnum = createSelector(
  [selectVisibleReportingScreenTabs, selectReportingDashboardCurrentApiTab],
  (visibleTabs, apiTab) => apiTabPositionToEnum(visibleTabs, apiTab) as ApiTabIndex,
);

export const selectReportingDashboardClients = createSelector(
  [reportingDashboardClientsSelector],
  ({ clients }) => clients,
);
export const selectReportingDashboardCurrentApiTabClients = createSelector(
  [selectReportingDashboardCurrentApiTabEnum, selectReportingDashboardClients],
  (apiTab, clients) => getClientsList(apiTab, clients as IClientControlOption<IParsedAdvertiser>[]),
);

const selectActiveClients = createSelector([activeClientsSelector], ({ clients }) => clients);

export const selectOmnichannelAdvertisers = createSelector([selectActiveClients], clients =>
  getAdvertisersList(ApiTabIndex.Omnichannel, clients as IClientControlOption<IParsedAdvertiser>[]),
);

export const selectGoogleAdvertisers = createSelector([selectActiveClients], clients =>
  getAdvertisersList(null, clients as IClientControlOption<IParsedAdvertiser>[]),
);

export const selectReportingDashboardWhitelabels = createSelector(
  [reportingDashboardWhitelabelsSelector],
  ({ whitelabels }) => whitelabels,
);
export const selectSharedApiWhitelabelsRequestId = (state: RootState) => state.sharedApi.whitelabelsRequestId;
export const selectReportingDashboardCurrentApiTabWhitelabels = createSelector(
  [selectReportingDashboardCurrentApiTabEnum, selectReportingDashboardWhitelabels],
  (apiTab, whitelabels) => getWhitelabelsList(apiTab, whitelabels),
);

export const selectReportingDashboardStoredFilters = createSelector(
  [selectReportingDashboard],
  ({ storedFilters, storedFiltersApi }) => ({ storedFilters, storedFiltersApi }),
);

//#region beeswax extended filters

export const selectReportingDashboardFilterCampaigns = createSelector(
  [selectReportingDashboardFilters],
  ({ campaigns }) => (campaigns ?? []) as IControlOption[],
);

export const selectReportingDashboardBeeswaxExtraFilterValues = createSelector(
  [selectReportingDashboardFilters],
  filters =>
    Object.values(ExtraBeeswaxFilterControlName).reduce(
      (acc, controlName) => {
        acc[controlName] = filters[controlName] ?? [];
        return acc;
      },
      {} as Record<ExtraBeeswaxFilterControlName, IExtraFilterControlOption[]>,
    ),
);

//#endregion
